/**
 * Account Aggregator Page
 *
 * React migration of the Angular AA list page with
 * listing, search, detail view, editing, and auth key management.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button } from 'equal-ds-ui';
import { Plus } from 'lucide-react';
import { AccountAggregatorTable } from './AccountAggregatorTable';
import { DrawerProvider } from 'equal-ds-ui';
import { AccountAggregatorViewDrawer } from './AccountAggregatorViewDrawer';
import { AccountAggregatorEditDrawer } from './AccountAggregatorEditDrawer';
import { AccountAggregatorAuthKeyDrawer } from './AccountAggregatorAuthKeyDrawer';
import { AccountAggregatorTokenDrawer } from './AccountAggregatorTokenDrawer';
import { AccountAggregatorUpdateAuthKeyDrawer } from './AccountAggregatorUpdateAuthKeyDrawer';
import { AccountAggregatorCreateDrawer } from './AccountAggregatorCreateDrawer';
import { useErrorHandler } from '../../../hooks/useErrorHandler';
import { Alert } from '../../../components/ui/Alert';
import { accountAggregatorAPI } from '../../../services/account-aggregator-api.service';
import { DEFAULT_PAGE_SIZE, DEFAULT_CURRENT_PAGE } from '../../../constants/pagination.constants';
import type {
  AccountAggregatorPageProps,
  AccountAggregatorRecord,
  AccountAggregatorPermissions,
  RegisterAccountAggregatorPayload,
  UpdateAccountAggregatorPayload,
} from '../../../types/account-aggregator';

interface UserInfo {
  role?: string;
  menuList?: Array<{
    code?: string;
    features?: Array<{ code?: string }>;
  }>;
}

const getUserInfo = (): UserInfo | null => {
  try {
    const encoded = sessionStorage.getItem('loggedUserInfo');
    if (!encoded) {
      return null;
    }
    return JSON.parse(atob(encoded));
  } catch (error) {
    console.error('[AccountAggregatorPage] Failed to parse user info:', error);
    return null;
  }
};

const normalizeRecord = (record: any): AccountAggregatorRecord => {
  const statusLabel =
    typeof record.status === 'string'
      ? record.status.toUpperCase()
      : record.status
        ? 'ACTIVE'
        : 'INACTIVE';

  const channelStatus =
    typeof record.channel_status === 'string'
      ? record.channel_status.toUpperCase() === 'ACTIVE'
      : Boolean(record.channel_status);

  const isStatusActive = statusLabel === 'ACTIVE';

  const sanitizeValue = (value: string | null | undefined) =>
    value && value.trim().length > 0 ? value : 'Not Available';

  return {
    ...record,
    status: isStatusActive,
    channel_status: channelStatus,
    AAStatus: isStatusActive ? 'active' : 'inactive',
    crOnboarding: channelStatus ? 'active' : 'inactive',
    base_url: sanitizeValue(record.base_url),
    web_url: sanitizeValue(record.web_url),
    apk_url: sanitizeValue(record.apk_url),
    aa_handle: sanitizeValue(record.aa_handle),
  };
};

const derivePermissions = (userInfo: UserInfo | null): AccountAggregatorPermissions => {
  if (!userInfo) {
    return { canView: false, canEdit: false, isSuperAdmin: false };
  }

  const isSuperAdmin = userInfo.role === 'superadmin';
  const menu = userInfo.menuList?.find((item) => item.code === 'ACCOUNT-AGGREGATOR');
  const features = menu?.features || [];
  const hasFeature = (code: string) =>
    features.some((feature) => feature.code?.trim() === code);

  return {
    canView: hasFeature('AccountAggregatorViewButton') || isSuperAdmin,
    canEdit: hasFeature('AccountAggregatorEDITButton') || isSuperAdmin,
    isSuperAdmin,
  };
};

export const AccountAggregatorPage: React.FC<AccountAggregatorPageProps> = ({
  routeParams,
  queryParams,
  currentRoute,
}) => {
  console.log('[AccountAggregatorPage] Mounted with props:', {
    routeParams,
    queryParams,
    currentRoute,
  });

  const userInfo = useMemo(() => getUserInfo(), []);
  const permissions = useMemo(() => derivePermissions(userInfo), [userInfo]);

  type DrawerType =
    | 'view'
    | 'edit'
    | 'generateAuth'
    | 'token'
    | 'updateAuth'
    | 'create'
    | null;

  const [aggregators, setAggregators] = useState<AccountAggregatorRecord[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(DEFAULT_CURRENT_PAGE);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [isLoading, setIsLoading] = useState(false);

  const [selectedAggregator, setSelectedAggregator] = useState<AccountAggregatorRecord | null>(null);
  const [drawerType, setDrawerType] = useState<DrawerType>(null);
  const [tokenValue, setTokenValue] = useState<string | null>(null);

  const [isEditSubmitting, setIsEditSubmitting] = useState(false);
  const [isAuthSubmitting, setIsAuthSubmitting] = useState(false);
  const [isUpdateSubmitting, setIsUpdateSubmitting] = useState(false);
  const [isCreateSubmitting, setIsCreateSubmitting] = useState(false);

  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const { error, errorMessage, handleError, clearError } = useErrorHandler();

  // Redirect loan officers to consent request (legacy behaviour)
  useEffect(() => {
    if (userInfo?.role === 'loanofficer') {
      window.location.hash = '#/dashboard/consentRequest';
    }
  }, [userInfo]);

  const fetchAggregators = useCallback(async () => {
    setIsLoading(true);
    clearError();

    try {
      const response = await accountAggregatorAPI.getAccountAggregators({
        limit: pageSize,
        pageno: currentPage,
      });

      const rows = response?.data?.rows || [];
      const normalized = rows.map(normalizeRecord);
      setAggregators(normalized);
      setTotalCount(response?.data?.count || 0);
    } catch (err) {
      handleError(err, { component: 'AccountAggregatorPage', action: 'fetchAggregators' });
      setAggregators([]);
      setTotalCount(0);
    } finally {
      setIsLoading(false);
    }
  }, [clearError, currentPage, handleError, pageSize]);

  useEffect(() => {
    fetchAggregators();
  }, [fetchAggregators]);

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  const handlePageSizeChange = useCallback((size: number) => {
    setPageSize(size);
    setCurrentPage(1);
  }, []);

  const openDrawer = useCallback(
    (type: DrawerType, record?: AccountAggregatorRecord | null) => {
      if (record !== undefined) {
        setSelectedAggregator(record);
      } else if (type === 'create') {
        setSelectedAggregator(null);
      }

      if (type !== 'token') {
        setTokenValue(null);
      }

      setDrawerType(type);
    },
    []
  );

  const closeDrawer = useCallback(() => {
    setDrawerType(null);
    setSelectedAggregator(null);
    setTokenValue(null);
  }, []);

  const handleEditSubmit = useCallback(
    async (payload: UpdateAccountAggregatorPayload) => {
      setIsEditSubmitting(true);
      setSuccessMessage(null);
      try {
        const response = await accountAggregatorAPI.updateAccountAggregator(payload);
        const message = response?.message || 'Account aggregator updated successfully.';
        setSuccessMessage(message);
        closeDrawer();
        await fetchAggregators();
      } catch (err) {
        handleError(err, { component: 'AccountAggregatorPage', action: 'updateAggregator' });
        throw err;
      } finally {
        setIsEditSubmitting(false);
      }
    },
    [closeDrawer, fetchAggregators, handleError]
  );

  const handleGenerateAuthKey = useCallback(
    async (expiredIn: number) => {
      if (!selectedAggregator) {
        return;
      }

      setIsAuthSubmitting(true);
      setSuccessMessage(null);

      try {
        const response = await accountAggregatorAPI.generateAuthKey({
          aa_id: selectedAggregator.account_aggregator_id,
          expiredIn,
        });

        const message = response?.message || 'Auth key generated successfully.';
        setSuccessMessage(message);
        setTokenValue(response?.data || null);
        setDrawerType('token');
      } catch (err) {
        handleError(err, { component: 'AccountAggregatorPage', action: 'generateAuthKey' });
        throw err;
      } finally {
        setIsAuthSubmitting(false);
      }
    },
    [handleError, selectedAggregator]
  );

  const handleUpdateAuthKey = useCallback(
    async (value: string) => {
      if (!selectedAggregator) {
        return;
      }

      setIsUpdateSubmitting(true);
      setSuccessMessage(null);

      try {
        const response = await accountAggregatorAPI.updateAuthKeyOutbound({
          aa_id: selectedAggregator.account_aggregator_id,
          auth_Key_outbound: value,
        });
        const message = response?.message || 'Outbound auth key updated successfully.';
        setSuccessMessage(message);
        closeDrawer();
        await fetchAggregators();
      } catch (err) {
        handleError(err, { component: 'AccountAggregatorPage', action: 'updateAuthKey' });
        throw err;
      } finally {
        setIsUpdateSubmitting(false);
      }
    },
    [closeDrawer, fetchAggregators, handleError, selectedAggregator]
  );

  const handleCreateAggregator = useCallback(
    async (payload: RegisterAccountAggregatorPayload) => {
      setIsCreateSubmitting(true);
      setSuccessMessage(null);

      try {
        const response = await accountAggregatorAPI.registerAccountAggregator(payload);
        const message = response?.message || 'Account aggregator created successfully.';
        setSuccessMessage(message);
        closeDrawer();
        await fetchAggregators();
      } catch (err) {
        handleError(err, { component: 'AccountAggregatorPage', action: 'registerAggregator' });
        throw err;
      } finally {
        setIsCreateSubmitting(false);
      }
    },
    [closeDrawer, fetchAggregators, handleError]
  );

  return (
    <div className="equal-ds-root h-full flex flex-col gap-4">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-text-primary">Account aggregators</h1>
          <p className="text-sm text-text-secondary mt-1">
            Review and manage account aggregator configuration and credentials.
          </p>
        </div>
        {permissions.isSuperAdmin && (
          <Button variant="primary" size="md" onClick={() => openDrawer('create')}>
            <Plus className="w-5 h-5 mr-2" />
            Create AA
          </Button>
        )}
      </header>

      {errorMessage && (
        <Alert variant="error">
          <div className="flex flex-col gap-1">
            <span>{errorMessage}</span>
            {error?.message && <span className="text-xs opacity-80">{error.message}</span>}
          </div>
        </Alert>
      )}

      {successMessage && (
        <Alert variant="success" onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      <AccountAggregatorTable
        data={aggregators}
        currentPage={currentPage}
        pageSize={pageSize}
        totalItems={totalCount}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        isLoading={isLoading}
        permissions={permissions}
        onViewDetails={(record) => openDrawer('view', record)}
        onEditDetails={(record) => openDrawer('edit', record)}
        onGenerateAuthKey={(record) => openDrawer('generateAuth', record)}
        onUpdateAuthKey={(record) => openDrawer('updateAuth', record)}
      />

      <DrawerProvider side="right" open={drawerType === 'view'} onOpenChange={(open) => { if (!open && drawerType === 'view') closeDrawer(); }}>
        <AccountAggregatorViewDrawer
          isOpen={drawerType === 'view'}
          record={selectedAggregator}
          onClose={closeDrawer}
        />
      </DrawerProvider>

      <DrawerProvider side="right" open={drawerType === 'edit'} onOpenChange={(open) => { if (!open && drawerType === 'edit') closeDrawer(); }}>
        <AccountAggregatorEditDrawer
          isOpen={drawerType === 'edit'}
          record={selectedAggregator}
          onSubmit={handleEditSubmit}
          onClose={closeDrawer}
          isSubmitting={isEditSubmitting}
        />
      </DrawerProvider>

      <DrawerProvider side="right" open={drawerType === 'generateAuth'} onOpenChange={(open) => { if (!open && drawerType === 'generateAuth') closeDrawer(); }}>
        <AccountAggregatorAuthKeyDrawer
          isOpen={drawerType === 'generateAuth'}
          aggregator={selectedAggregator}
          isSubmitting={isAuthSubmitting}
          onSubmit={handleGenerateAuthKey}
          onClose={closeDrawer}
        />
      </DrawerProvider>

      <DrawerProvider side="right" open={drawerType === 'token'} onOpenChange={(open) => { if (!open && drawerType === 'token') closeDrawer(); }}>
        <AccountAggregatorTokenDrawer
          isOpen={drawerType === 'token'}
          token={tokenValue}
          aggregatorId={selectedAggregator?.account_aggregator_id || null}
          onClose={closeDrawer}
        />
      </DrawerProvider>

      <DrawerProvider side="right" open={drawerType === 'updateAuth'} onOpenChange={(open) => { if (!open && drawerType === 'updateAuth') closeDrawer(); }}>
        <AccountAggregatorUpdateAuthKeyDrawer
          isOpen={drawerType === 'updateAuth'}
          aggregator={selectedAggregator}
          onSubmit={handleUpdateAuthKey}
          onClose={closeDrawer}
          isSubmitting={isUpdateSubmitting}
        />
      </DrawerProvider>

      <DrawerProvider side="right" open={drawerType === 'create'} onOpenChange={(open) => { if (!open && drawerType === 'create') closeDrawer(); }}>
        <AccountAggregatorCreateDrawer
          isOpen={drawerType === 'create'}
          onSubmit={handleCreateAggregator}
          onClose={closeDrawer}
          isSubmitting={isCreateSubmitting}
        />
      </DrawerProvider>
    </div>
  );
};
